import { Inject, Injectable, NgZone } from '@angular/core'
import { DepartmentMapFilterService } from './department-map-filter.service'
import { MapColorGeneratorService } from '../../../../../features/map/services/map-color-generator.service'
import { IFilterDefTypeEnum } from '../../../../../shared/open-layers/layer-filter.interface'
import { ContractGroupDto } from '../../../../../api/backend'
import { MapColorGeneratorActions } from '../../../../../features/map/common/map-color-generator/state/action-types'
import { Store } from '@ngrx/store'
import {
    selectMapColorGeneratorColorMapByKey,
} from '../../../../../features/map/common/map-color-generator/state/map-color-generator.selectors'
import { filter, take, map } from 'rxjs/operators'
import { Observable, firstValueFrom } from 'rxjs'

@Injectable({
    providedIn: 'root'
})
export class GeneralMapLayerDefinitionsContractGroupService {
    colorList = {}

    constructor(
        @Inject(Store) private store: Store,
        @Inject(DepartmentMapFilterService) private departmentMapFilterService: DepartmentMapFilterService,
        @Inject(MapColorGeneratorService) private mapColorGeneratorService: MapColorGeneratorService,
        @Inject(NgZone) private ngZone: NgZone,
    ) {
    }

    public getLayers(contractGroups: Array<ContractGroupDto>): Promise<any> {
        if (!contractGroups?.length) {
            return Promise.resolve([])
        }

        // Convert Observable to Promise using toPromise() to maintain backward compatibility
        return firstValueFrom(this.initColorList(contractGroups).pipe(
            map(() => {
                const layers = []
                contractGroups.forEach(contractGroup => {
                    const color = this.colorList[contractGroup.id]
                    const layer = {
                        id: 'contract_group_' + contractGroup.id,
                        type: 'wms',
                        title: contractGroup.name,
                        selector: 'switch',
                        visible: false,
                        selectorStyle: 'custom-icon-color',
                        styleAttribute: this.mapColorGeneratorService.getMapLayerIconStyle(color.fill, color.stroke)
                    }
                    layers.push(layer)
                })
                return layers
            })
        ))
    }

    public getContractGroupLayer(id, url, layerPrefix): any {
        const c = this.mapColorGeneratorService.getHexColor(this.colorList[id].mapColor)
        return {
            id: 'contract_group_' + id,
            layer: {
                type: 'wms',
                url: url,
                reload: true,
                wms: {
                    layers: layerPrefix + 'contract_group',
                    format: 'image/png8',
                    transparent: true,
                    unloadInvisibleTiles: true,
                    noWrap: true,
                    zIndex: 360
                },
                filter: this.departmentMapFilterService.updateMapFilterDepartmentIdEqual({
                    type: IFilterDefTypeEnum.PropertyIsEqualTo,
                    propertyName: 'contract_group_id',
                    literal: id
                }),
                sldBody: {
                    name: layerPrefix + 'contract_group',
                    rules: [
                        {
                            name: 'contract_group_' + id,
                            polygonSymbolizer: {
                                fill: {
                                    svgParameter: {
                                        'fill': c,
                                        'fill-opacity': this.colorList[id].mapOpacity
                                    }
                                },
                                stroke: {
                                    svgParameter: {
                                        'stroke': c,
                                        'stroke-width': 1
                                    }
                                }
                            }
                        }
                    ]
                }
            }
        }
    }

    // ---------

    private initColorList(contractGroups: Array<ContractGroupDto>): Observable<void> {
        this.ngZone.run(() => {
            this.store.dispatch(MapColorGeneratorActions.getNextColors({ num: contractGroups.length, ident: 'contract_group' }))
        })

        return this.store.select(selectMapColorGeneratorColorMapByKey('contract_group')).pipe(
            filter((colors: any[]) => colors && colors.length > 0),
            take(1),
            map(colors => {
                for (let i = 0; i < contractGroups.length; i++) {
                    this.colorList[contractGroups[i].id] = colors[contractGroups.length - i - 1]
                }
            })
        )
    }
}